<script setup lang="ts">
import { inject, computed, type ComputedRef } from "vue";
import { useScreen } from "@/composable/useResponsive";
import type { MenuEntity } from "./menu.types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

withDefaults(
  defineProps<{
    item: MenuEntity;
    type?: "primary" | "params" | "dark";
  }>(),
  {
    type: "primary",
  }
);

const { screen } = useScreen();
const menuMinimal = inject<ComputedRef<boolean | undefined>>("menuMinimal");

// Use injected minimal prop if provided, otherwise fallback to screen-based detection
const isMinimal = computed(() => {
  if (menuMinimal?.value !== undefined) {
    return menuMinimal.value;
  }
  return screen.value.lt.lg;
});
</script>

<template>
  <el-menu-item
    v-if="type === 'dark'"
    :index="item.index"
    :route="item.route"
    class="rounded dark-item"
    :style="{
      height: '3rem',
    }"
  >
    <div
      :class="[
        isMinimal
          ? 'w-100 d-flex flex-column gap-1 justify-content-center align-items-center'
          : 'd-flex gap-2 align-items-center',
      ]"
      :style="
        item.highlight?.color
          ? { color: `var(--bs-${item.highlight.color})` }
          : {}
      "
    >
      <PhosphorIcon v-if="item.icon" :name="item.icon" :size="22" />

      <span v-if="!isMinimal" class="text-truncate col">{{
        $t(item.label)
      }}</span>
      <el-icon v-if="item.highlight?.icon" class="position-absolute end-0 me-2"
        ><i :class="item.highlight.icon"
      /></el-icon>
      <el-badge
        v-if="item.highlight?.value !== undefined"
        class="position-absolute end-0 me-2"
        :value="item.highlight.value"
        :type="item.highlight.color ?? 'info'"
      />
    </div>
  </el-menu-item>

  <el-menu-item
    v-if="type === 'primary'"
    :index="item.index"
    :route="item.route"
    class="rounded primary-item"
    :style="{
      height: '3rem',
    }"
  >
    <div
      :class="[
        isMinimal
          ? 'w-100 d-flex flex-column gap-1 justify-content-center align-items-center'
          : 'd-flex gap-2 align-items-center',
      ]"
      :style="
        item.highlight?.color
          ? { color: `var(--bs-${item.highlight.color})` }
          : {}
      "
    >
      <PhosphorIcon v-if="item.icon" :name="item.icon" :size="22" />

      <span v-if="!isMinimal" class="text-truncate col">{{
        $t(item.label)
      }}</span>
      <el-icon v-if="item.highlight?.icon" class="position-absolute end-0 me-2"
        ><i :class="item.highlight.icon"
      /></el-icon>
      <el-badge
        v-if="item.highlight?.value !== undefined"
        class="position-absolute end-0 me-2"
        :value="item.highlight.value"
        :type="item.highlight.color ?? 'info'"
      />
    </div>
  </el-menu-item>

  <el-menu-item
    v-if="type === 'params'"
    :index="item.index"
    :route="item.route"
    class="rounded"
    :style="{
      height: '3rem',
    }"
  >
    <div
      class="d-flex gap-2 align-items-center"
      :style="
        item.highlight?.color
          ? { color: `var(--bs-${item.highlight.color})` }
          : {}
      "
    >
      <PhosphorIcon v-if="item.icon" :name="item.icon" :size="18" />

      <span class="text-truncate col">{{ $t(item.label) }}</span>
      <el-icon v-if="item.highlight?.icon" class="position-absolute end-0 me-2"
        ><i :class="item.highlight.icon"
      /></el-icon>
      <el-badge
        v-if="item.highlight?.value !== undefined"
        class="position-absolute end-0 me-2"
        :value="item.highlight.value"
        :type="item.highlight.color ?? 'info'"
      />
    </div>
  </el-menu-item>
</template>

<style lang="scss">
.el-menu-item {
  line-height: 1;
}

.primary-item.is-active {
  background-color: var(--bs-light);
  color: var(--bs-primary);
}

.dark-item {
  color: white !important;
}
.dark-item.is-active {
  background-color: var(--bs-light);
  color: var(--bs-dark) !important;
}
</style>
